// Dialer session store. Module-level, so a live call, its notes and the forced disposition survive moving between
// pages (Tasks, Leads…) and back. All side effects (queue, prepare, Twilio connect, disposition) happen here; the call
// state itself only changes through machine.transition(), which ignores anything out of order.
//
// Hard rules enforced here (SPEC §15.1):
// - No auto-dial, ever: dial() runs only from a human keypress/click. Loading the next lead never dials it.
// - One call at a time: machine.canDial() + PhoneManager.whyCantCall() + Twilio's own "A Call is already active".
// - A disposition is required before the next lead; /api/calls/pending is checked on load and after every call.
// - Enter = save and dial the next lead (REVIEW-2026-10-01): still one human keypress per dial, never a dial on its own.
// - C = "Call back now" (2026-10-06): save the call (if owed) and redial the same number, one keypress, never on its own.
// - A number called recently (PJ 2026-10-20) asks "Call this number again?" first: Dial opens the dialog, and only its
//   Yes (a second human keypress or click) dials, with redialConfirmed. No leaves the lead as it was.
// - "Record (they said yes)" (PJ 2026-10-20): only on a connected call whose prepare answer offered it; the button opens a
//   confirm that says to ask first, and only "They said yes, start" asks the server to record. Never on its own.
import type { Call } from '@twilio/voice-sdk'; // type only: the SDK itself is loaded lazily by the phone manager
import type {
  Business, CallBackOffer, CallPrepareRequest, CallPrepareResponse, Campaign, DispositionCode, DispositionRequest, GateConfirm,
  GateReason, GateReasonCode, GateResult, Meeting, QueueItem, VmRecording,
} from '../../../src/shared/api';
import type { DrawerData, DrawerHook, LiveCheck, SearchStatus } from '../../../src/shared/api-leadgen';
import type { CallPace } from '../../../src/shared/api-dialer';
import type { DropRefusal } from '../../../src/shared/api-telephony';
import { dropGateBeforeLineCheck } from '../../../src/shared/api-telephony';
import { api, ApiFailure, errorMessage } from '../api';
import {
  cachedDrawer, cachedLive, dropLive, prefetchDrawer, putDrawer, requestResearch, resetDrawerCache, runLiveCheck, verifyObservation,
} from './drawer/data';
import {
  hookView, partsOnAppear, partsOnConnect, partsOnOpen, partsOnRecheck, sameSentence, shownHookId, type DrawerHookBlock, type LivePart,
} from './drawer/view';
import { toast } from '../components/ui';
import { callClosed, phone } from '../phone/manager';
import { explainPhoneError, type PhoneErrorLike } from '../phone/errors';
import { endpoints, gateFromError } from './endpoints';
import { CALL_BACK_NOW_NOTE, callBackLoggedProblem, callBackOutcomeProblem, DISPOSITION_META, fmtPhone, type HandPickCount } from './labels';
import { canDial, canLeaveLead, INITIAL_CALL, isLive, transition, type CallEvent, type CallMachine } from './machine';
import { pickDropRecording, shownOpener, verifiedObservation } from './script';
import type { DialerSettings, TodayReport } from './types';
import { CONSENT_RECORDING_ON, phaseAfterRefusal, type ConsentRecPhase, type ConsentRecUi } from './consentRecord';
import {
  buildDisposition as buildRequest, callbackRefusal, defaultOutcome, effectiveSpokeToDm, emptyWrap as emptyWrapFor, noCampaignProblem,
  settledOutcome, wrapProblem as wrapProblemFor, type WrapUp,
} from './wrap';
import type { DialTarget, QueueItemNumbers } from '../../../src/shared/api-numbers';
import { OTHER_LINE_OUTCOMES, e164For, gateFor, numberOf, otherLineButton, otherLineNote, storeButton, targetOfNumber } from './numbers';

export type { WrapUp };
export { effectiveSpokeToDm };

export interface DialerState {
  view: 'picker' | 'lead';
  campaignId: number | null;
  campaign: Campaign | null;
  campaignError: string | null;
  item: QueueItem | null;
  lead: 'none' | 'loading' | 'ready' | 'empty' | 'error';
  leadError: string | null;
  empty: {
    remainingEligible: number; blockedCounts: Partial<Record<GateReasonCode, number>>;
    nextRetryLabel: string | null; waitingCount: number;
  } | null;
  /** Leads queue/next never serves (hand-pick-only states such as Oklahoma), from its latest answer for this campaign. */
  handPick: { total: number; states: HandPickCount[] } | null;
  skipIds: number[];
  call: CallMachine;
  wrap: WrapUp;
  bookedMeeting: Meeting | null;
  lastSaved: { code: DispositionCode; business: string; at: number } | null;
  pending: 'unchecked' | 'checking' | 'clear' | 'error';
  pendingError: string | null;
  recordings: VmRecording[] | null;
  recordingsError: string | null;
  today: TodayReport | null;
  settings: DialerSettings | null;   // company-wide values the dialer needs (mailing address for follow-up email)
  settingsError: string | null;
  dialerVisible: boolean;
  /** The in-call Business drawer for the lead on screen (LEADGEN-SPEC §11). Page memory only. */
  drawer: DrawerUi;
  /** REVIEW-2026-10-01 §4.4: my live pace (GET /api/calls/pace), refreshed on load, after each save and every minute. */
  pace: CallPace | null;
  /** When I last pressed Dial on this screen (ms), so "since last dial" restarts before the server's next count. */
  lastDialAt: number | null;
  /** Opener A/B: the opener picked for the lead on screen. Set by the selector, and fixed to the one on screen at Dial. */
  openerPick: { membershipId: number; label: string } | null;
  /** "Call back now" (2026-10-06) for the lead on screen: the wrap-up's check, or the offer that came with the item. */
  callBack: CallBackUi | null;
  /** "Call this number again?" (PJ 2026-10-20) while it is on screen (RedialConfirm, mounted once in PhoneProvider). */
  redialConfirm: RedialConfirmUi | null;
  /** "Record (they said yes)" (PJ 2026-10-20) for the call prepared last, when its prepare answer offered it. */
  consentRec: ConsentRecUi | null;
}

/** The "Call this number again?" dialog: which lead, and what the server's gate said (GateResult.confirm). */
export interface RedialConfirmUi {
  leadKey: number;              // leadKeyOf(item) of the lead it would dial
  business: string;
  phone: string;                // formatted, e.g. (404) 555-0100
  prompt: string;               // GateConfirm.prompt: "You called this number 1 min ago (2nd call in 24 hours). Call again?"
  reasons: GateReason[];        // the soft reasons, as the lead card lists them
  meeting: boolean;             // GateConfirm.meeting (PJ 2026-10-28): the lead has a meeting booked; titled "Meeting booked"
}

/** "Call back now" for one lead: which call it redials, until when, and what the server's gate said. */
export interface CallBackUi {
  leadKey: number;              // leadKeyOf(item) of the lead it belongs to
  callId: number;               // the call it calls back
  until: number;                // ms epoch: 15 minutes after that call ended
  callerId: string | null;      // the caller ID it redials from (the same number)
  gate: GateResult | null;      // null while the check is in flight
  error: string | null;         // the check failed, or the server said there is nothing to call back
  /** C was pressed for it (or the server refused at press time): only then is a refusal spelled out under the call
   *  bar; before that it is the disabled button's hover text. */
  pressed: boolean;
  /** PJ 2026-10-23: which of the lead's numbers that call went to (the redial goes to the same one); null = its one
   *  number. */
  target?: DialTarget | null;
}

/** How long after a call "Call back now" stays on offer (the server's QUICK_CALLBACK_WINDOW_MS decides). */
export const CALL_BACK_WINDOW_MS = 15 * 60_000;

/** One number per lead on screen: the membership id, or minus the business id for a no-campaign item (membership 0). */
export function leadKeyOf(item: QueueItem): number {
  return item.noCampaign ? -item.business.id : item.membership.id;
}

function callBackFromOffer(leadKey: number, o: CallBackOffer): CallBackUi {
  const until = Date.parse(o.until);
  return {
    leadKey, callId: o.callId, until: Number.isFinite(until) ? until : 0, callerId: o.callerId, gate: o.gate, error: null, pressed: false,
    target: o.target ?? null,
  };
}

/**
 * Drawer state for the lead on screen. `data` is the D1 snapshot (GET /api/businesses/:id/drawer, prefetched when the
 * lead appears); `live` is the newest live check and is dropped when the lock is released or another lead loads.
 */
export interface DrawerUi {
  open: boolean;
  businessId: number | null;
  membershipId: number | null;
  data: DrawerData | null;
  loading: boolean;
  error: string | null;          // GET drawer failed (the lead card and calling are unaffected)
  live: LiveCheck | null;
  liveInFlight: number;
  liveError: string | null;      // the live-check request itself failed (network, HTTP, quota refusal)
  placesTried: boolean;          // one automatic Places check per lead; R re-runs it on purpose
  verifying: boolean;
}

export const NO_DRAWER: DrawerUi = {
  open: false, businessId: null, membershipId: null, data: null, loading: false, error: null, live: null, liveInFlight: 0,
  liveError: null, placesTried: false, verifying: false,
};

function emptyWrap(item: QueueItem | null): WrapUp {
  return emptyWrapFor(item?.business ?? null);
}

const INITIAL: DialerState = {
  view: 'picker', campaignId: null, campaign: null, campaignError: null, item: null, lead: 'none', leadError: null,
  empty: null, handPick: null, skipIds: [], call: INITIAL_CALL, wrap: emptyWrap(null), bookedMeeting: null, lastSaved: null,
  pending: 'unchecked', pendingError: null, recordings: null, recordingsError: null, today: null,
  settings: null, settingsError: null, dialerVisible: false, drawer: NO_DRAWER, pace: null, lastDialAt: null, openerPick: null,
  callBack: null, redialConfirm: null, consentRec: null,
};

let state: DialerState = INITIAL;
const listeners = new Set<() => void>();
let leadSeq = 0;
let cancelRequested = false; // Hang up pressed while /api/calls/prepare was in flight
/** The outcome onEnded pre-selected (No answer on an unanswered call) that the setter hasn't touched: "Call back now"
 *  treats it as nothing picked and adds its note. */
let autoOutcome: { callId: number; code: DispositionCode } | null = null;
/** The voicemail drop POST in flight, so onEnded can wait for its answer before asking /api/calls/pending. */
let dropInFlight: { callId: number; done: Promise<void> } | null = null;
/** What "Yes, call" runs for the dialog on screen (state.redialConfirm). A function, so it lives here, not in the state. */
let redialYes: (() => void) | null = null;
/**
 * How long onEnded waits for an in-flight drop. The Worker answers /drop after the Twilio redirect plus a few D1 writes;
 * past this the wrap-up is shown, and a late success still clears it (machine: DROP_FINISHED in needs_disposition).
 */
export const DROP_WAIT_MS = 15_000;

export function getDialer(): DialerState { return state; }
export function subscribeDialer(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
function set(patch: Partial<DialerState>): void {
  state = { ...state, ...patch };
  for (const fn of listeners) fn();
  // A different lead on screen (or none): prefetch its drawer and drop the previous lead's live data. The lead card has
  // already rendered from the line above, so the drawer never delays it.
  if ('item' in patch) syncDrawer();
}
function dispatch(e: CallEvent): void {
  const next = transition(state.call, e);
  if (next !== state.call) set({ call: next });
}

const LAST_CAMPAIGN = 'vd.dialer.campaign';
export function rememberedCampaignId(): number | null {
  try { const n = Number(localStorage.getItem(LAST_CAMPAIGN)); return Number.isInteger(n) && n > 0 ? n : null; } catch { return null; }
}

export function setDialerVisible(v: boolean): void { if (state.dialerVisible !== v) set({ dialerVisible: v }); }

/** Signed out: forget the session (the phone manager is stopped separately). */
export function resetDialer(): void {
  leadSeq++;
  dropInFlight = null;
  autoOutcome = null;
  redialYes = null;
  drawerSeq++;
  stopResearchPoll();
  resetDrawerCache();
  state = { ...INITIAL };
  for (const fn of listeners) fn();
}

// ------------------------------------------------------------------ startup: unfinished call

/** SPEC §8.2: an undispositioned call from the last 12 h must be logged before anything else. */
export async function checkPending(): Promise<void> {
  if (state.pending === 'checking') return;
  if (isLive(state.call) || state.call.phase === 'needs_disposition' || state.call.phase === 'preparing') { set({ pending: 'clear' }); return; }
  set({ pending: 'checking', pendingError: null });
  let p;
  try {
    p = await endpoints.pending();
  } catch (e) {
    set({ pending: 'error', pendingError: `Couldn't check for an unfinished call: ${errorMessage(e)}` });
    return;
  }
  if (!p) { set({ pending: 'clear' }); return; }
  if (p.membershipId === null && p.noCampaign && p.businessId !== null) {
    // SPEC §19 (2026-10-02): a no-campaign pad call is owed. It has no lead row; reopen it by its business.
    try {
      const item = await endpoints.noCampaignItem(p.businessId);
      set({ item, lead: 'ready', view: 'lead', wrap: { ...emptyWrap(item), code: null }, pending: 'clear' });
      dispatch({ type: 'RESTORE_PENDING', callId: p.id, membershipId: 0, to: p.toE164 ?? null });
      void loadCallBack(p.id);
      void loadSettings();
      toast(`Log the outcome of your last call to ${item.business.name} (no campaign): save it to My leads or not.`, 'warn');
    } catch (e) {
      set({ pending: 'error', pendingError: `You have an unfinished call${p.businessName ? ` to ${p.businessName}` : ''}, but it couldn't be opened: ${errorMessage(e)}` });
    }
    return;
  }
  if (p.membershipId === null) {
    // Every dialer call has a lead; if one doesn't, fail closed (the server also refuses new dials until it's logged).
    set({ pending: 'error', pendingError: `Your last call (#${p.id}${p.businessName ? ` to ${p.businessName}` : ''}) isn't linked to a lead, so it can't be logged here. Tell an admin.` });
    return;
  }
  const membershipId = p.membershipId;
  try {
    const item = await endpoints.queueItem(membershipId);
    const campaignId = item.campaign.id;
    set({ item, lead: 'ready', view: 'lead', campaignId, wrap: { ...emptyWrap(item), code: null }, pending: 'clear' });
    dispatch({ type: 'RESTORE_PENDING', callId: p.id, membershipId, to: p.toE164 ?? null });
    void loadCallBack(p.id);
    void loadCampaign(campaignId);
    void loadRecordings();
    void loadSettings();
    toast(`Log the outcome of your last call to ${item.business.name} before the next lead.`, 'warn');
  } catch (e) {
    set({ pending: 'error', pendingError: `You have an unfinished call${p.businessName ? ` to ${p.businessName}` : ''}, but it couldn't be opened: ${errorMessage(e)}` });
  }
}

// ------------------------------------------------------------------ campaign and lead loading

async function loadCampaign(id: number): Promise<void> {
  if (state.campaign?.id === id) return;
  try {
    const campaign = await endpoints.campaign(id);
    if (state.campaignId === id) set({ campaign, campaignError: null });
  } catch (e) {
    if (state.campaignId === id) set({ campaignError: `Couldn't load this campaign's script: ${errorMessage(e)}` });
  }
}

export async function loadRecordings(force = false): Promise<void> {
  if (state.recordings && !force) return;
  try {
    set({ recordings: await endpoints.recordings(), recordingsError: null });
  } catch (e) {
    set({ recordings: [], recordingsError: `Couldn't load your voicemail recordings: ${errorMessage(e)}` });
  }
}

/** Company settings (GET /api/settings, readable by setters). Without them the follow-up email stays off. */
export async function loadSettings(force = false): Promise<void> {
  if (state.settings && !force) return;
  try {
    const v = await endpoints.settings();
    const address = typeof v.mailingAddress === 'string' && v.mailingAddress.trim() ? v.mailingAddress.trim() : null;
    set({ settings: { mailingAddress: address }, settingsError: null });
  } catch (e) {
    set({ settingsError: `Couldn't load the company settings, so the follow-up email is off: ${errorMessage(e)}` });
  }
}

export async function refreshToday(): Promise<void> {
  void refreshPace();
  try { set({ today: await endpoints.today() }); } catch { /* stats are a nicety; the dial block goes on */ }
}

/** My live pace for the top bar. A nicety: a failure leaves the last numbers up and the dial block goes on. */
export async function refreshPace(): Promise<void> {
  try {
    const pace = await endpoints.pace();
    if (pace && typeof pace === 'object') set({ pace });
  } catch { /* stats are a nicety */ }
}

export async function startCampaign(campaignId: number): Promise<void> {
  if (state.pending !== 'clear') { toast('Still checking for an unfinished call; one moment.', 'warn'); return; }
  if (!canLeaveLead(state.call)) { toast('Finish the current call first.', 'warn'); return; }
  try { localStorage.setItem(LAST_CAMPAIGN, String(campaignId)); } catch { /* not remembered */ }
  if (state.item && state.campaignId !== campaignId) releaseQuietly(state.item.membership.id);
  set({ view: 'lead', campaignId, campaign: state.campaign?.id === campaignId ? state.campaign : null, campaignError: null,
    item: state.campaignId === campaignId ? state.item : null, skipIds: state.campaignId === campaignId ? state.skipIds : [],
    handPick: state.campaignId === campaignId ? state.handPick : null });
  void loadCampaign(campaignId);
  void loadRecordings();
  void loadSettings();
  if (!state.item) await loadNext();
}

function releaseQuietly(membershipId: number): void {
  // The lock is going: its live data goes with it (LEADGEN-SPEC §11), before the next lead even arrives.
  const d = state.drawer;
  if (d.membershipId === membershipId && d.businessId !== null) {
    dropLive(d.businessId);
    set({ drawer: { ...d, live: null, liveInFlight: 0, liveError: null } });
  }
  if (membershipId <= 0) return; // a no-campaign item (SPEC §19) has no lead row and no lock
  endpoints.queueRelease(membershipId).catch((e) => console.warn('queue release failed', membershipId, errorMessage(e)));
}

function showItem(item: QueueItem): void {
  // A "Call this number again?" left open for another lead is void once this one is on screen.
  if (state.redialConfirm && state.redialConfirm.leadKey !== leadKeyOf(item)) cancelRedial();
  set({
    item, lead: 'ready', leadError: null, empty: null, wrap: emptyWrap(item), bookedMeeting: null, openerPick: null,
    callBack: item.callBack ? callBackFromOffer(leadKeyOf(item), item.callBack) : null,
  });
  dispatch({ type: 'RESET' });
}

/** queue/next's hand-pick counts; null when there are none (or the server doesn't send them). */
export function handPickFrom(res: { handPickWaiting?: number; handPickStates?: HandPickCount[] }): DialerState['handPick'] {
  const total = Number(res.handPickWaiting ?? 0);
  if (!Number.isFinite(total) || total <= 0) return null;
  return { total, states: Array.isArray(res.handPickStates) ? res.handPickStates : [] };
}

/** Loads the next eligible lead. Never dials it. */
export async function loadNext(): Promise<void> {
  const campaignId = state.campaignId;
  if (!campaignId || !canLeaveLead(state.call) || state.lead === 'loading') return;
  const mine = ++leadSeq;
  set({ lead: 'loading', leadError: null });
  try {
    const res = await endpoints.queueNext(campaignId, state.skipIds);
    if (mine !== leadSeq) { if (res.item) releaseQuietly(res.item.membership.id); return; }
    set({ handPick: handPickFrom(res) });
    if (res.item) showItem(res.item);
    else {
      set({
        item: null, lead: 'empty', wrap: emptyWrap(null),
        empty: {
          remainingEligible: res.remainingEligible, blockedCounts: res.blockedCounts ?? {},
          nextRetryLabel: res.nextRetryLabel ?? null, waitingCount: res.waitingCount ?? 0,
        },
      });
      dispatch({ type: 'RESET' });
    }
  } catch (e) {
    if (mine === leadSeq) set({ lead: 'error', leadError: `Couldn't load the next lead: ${errorMessage(e)}` });
  }
}

/** Skip this lead without calling (also the "Skip" offered on a blocked dial). */
export async function skip(): Promise<void> {
  const item = state.item;
  if (!item || !canLeaveLead(state.call) || state.lead === 'loading') return;
  if (item.noCampaign) { await leaveNoCampaign(); return; }
  releaseQuietly(item.membership.id);
  set({ skipIds: [...state.skipIds, item.membership.id] });
  dispatch({ type: 'RESET' });
  await loadNext();
}

/** Off a no-campaign item (SPEC §19): back to the campaign session it interrupted (next lead, never dialled), else to
 *  the campaign picker. */
async function leaveNoCampaign(): Promise<void> {
  leadSeq++;
  dispatch({ type: 'RESET' });
  if (state.campaignId) {
    set({ item: null, lead: 'none', leadError: null, wrap: emptyWrap(null), bookedMeeting: null, openerPick: null });
    await loadNext();
  } else {
    set({ view: 'picker', item: null, lead: 'none', empty: null, wrap: emptyWrap(null), bookedMeeting: null, openerPick: null });
    void refreshToday();
  }
}

/** "Include skipped leads" on the empty state. */
export async function clearSkipsAndReload(): Promise<void> {
  if (!canLeaveLead(state.call)) return;
  set({ skipIds: [] });
  await loadNext();
}

/**
 * Open a specific lead (from Tasks or Leads). Gated at prepare like any other.
 * 'busy' = a call is live or its outcome is owed (the caller should show the dialer); 'failed' = couldn't load (toasted).
 */
export async function openMembership(membershipId: number): Promise<'opened' | 'busy' | 'failed'> {
  const ready = await readyToOpen();
  if (ready !== 'ok') return ready;
  const mine = ++leadSeq;
  const previous = state.item;
  set({ lead: 'loading', leadError: null, view: 'lead' });
  try {
    const item = await endpoints.queueItem(membershipId);
    if (mine !== leadSeq) return 'failed';
    adoptItem(item, previous);
    return 'opened';
  } catch (e) {
    if (mine === leadSeq) set({ lead: previous ? 'ready' : 'error', leadError: `Couldn't open that lead: ${errorMessage(e)}` });
    toast(`Couldn't open that lead: ${errorMessage(e)}`, 'bad');
    return 'failed';
  }
}

/** A saved no-campaign number from My leads (SPEC §19, 2026-10-02), on screen without dialing it. */
export async function openNoCampaign(businessId: number): Promise<'opened' | 'busy' | 'failed'> {
  const ready = await readyToOpen();
  if (ready !== 'ok') return ready;
  const mine = ++leadSeq;
  const previous = state.item;
  try {
    const item = await endpoints.noCampaignItem(businessId);
    if (mine !== leadSeq) return 'failed';
    adoptItem(item, previous);
    return 'opened';
  } catch (e) {
    toast(`Couldn't open that number: ${errorMessage(e)}`, 'bad');
    return 'failed';
  }
}

/** Dial pad (SPEC §19): put a lead the server already loaded (POST /api/calls/manual) on screen, as openMembership
 *  would. It never dials; the pad calls dial() next, which runs the gated prepare like any queue lead. */
export async function openItem(item: QueueItem): Promise<'opened' | 'busy' | 'failed'> {
  const ready = await readyToOpen();
  if (ready !== 'ok') return ready;
  leadSeq++;
  adoptItem(item, state.item);
  return 'opened';
}

async function readyToOpen(): Promise<'ok' | 'busy' | 'failed'> {
  if (!canLeaveLead(state.call)) { toast('Finish the current call and log its outcome first.', 'warn'); return 'busy'; }
  if (state.pending !== 'clear') {
    await checkPending();
    const after = getDialer(); // re-read: checkPending() replaced the state
    if (!canLeaveLead(after.call)) return 'busy';
    if (after.pending !== 'clear') { toast(after.pendingError ?? "Couldn't check for an unfinished call.", 'bad'); return 'failed'; }
  }
  return 'ok';
}

function adoptItem(item: QueueItem, previous: QueueItem | null): void {
  if (previous && previous.membership.id !== item.membership.id) releaseQuietly(previous.membership.id);
  if (item.noCampaign) {
    // No campaign (SPEC §19): the dialer's campaign session (if any) stays as it was, to go back to after this call.
    set({ view: 'lead' });
    showItem(item);
    void loadSettings();
    return;
  }
  const campaignId = item.campaign.id;
  try { localStorage.setItem(LAST_CAMPAIGN, String(campaignId)); } catch { /* not remembered */ }
  set({ view: 'lead', campaignId, campaign: state.campaign?.id === campaignId ? state.campaign : null,
    skipIds: state.campaignId === campaignId ? state.skipIds : [], handPick: state.campaignId === campaignId ? state.handPick : null });
  showItem(item);
  void loadCampaign(campaignId);
  void loadRecordings();
  void loadSettings();
}

/** Back to the campaign picker. */
export function stopDialing(): void {
  if (!canLeaveLead(state.call)) { toast('Finish the current call and log it first.', 'warn'); return; }
  leadSeq++;
  if (state.item) releaseQuietly(state.item.membership.id);
  set({ view: 'picker', item: null, lead: 'none', empty: null, handPick: null, skipIds: [], wrap: emptyWrap(null) });
  dispatch({ type: 'RESET' });
  void refreshToday();
}

// ------------------------------------------------------------------ calling

// ------------------------------------------------------------------ "Call this number again?" (PJ 2026-10-20)
//
// PJ: "It should be just warnings on dial backs saying that, hey, you have called this back. Do you still want to dial
// it? And then you should be able to click yes or no." A gate whose every failing reason is a soft attempt rule (the 4 h
// spacing, 2 a day, 3 a week, the campaign's attempt cap, a lead out of attempts) carries GateResult.confirm: the card
// shows it amber, Dial reads "Call again", and pressing it opens the dialog. Yes dials with redialConfirmed; No closes.
// Every other reason (DNC, hours, the 3-a-day cap, another setter's lock...) blocks exactly as before. PJ 2026-10-28: a
// lead with a meeting booked is a warning too (GateConfirm.meeting): "Meeting booked. Call anyway?", then Yes / No.

/** The gate's confirm when a yes would let the dial through, else null (a hard block, or nothing to confirm). */
export function confirmableGate(g: GateResult | null | undefined): GateConfirm | null {
  return g && !g.ok && g.confirm && typeof g.confirm.prompt === 'string' && Array.isArray(g.confirm.reasons) ? g.confirm : null;
}

/** Opens "Call this number again?" for `item`; "Yes, call" runs `onYes` (only while that lead is the one asked about).
 *  Nothing dials until then. `e164` (PJ 2026-10-23): the number it would call (the owner's or the store's); absent =
 *  the lead's default dial. */
export function askRedial(item: QueueItem, confirm: GateConfirm, onYes: () => void, e164?: string): void {
  redialYes = onYes;
  set({
    redialConfirm: {
      leadKey: leadKeyOf(item), business: item.business.name, phone: fmtPhone(e164 ?? e164For(item)),
      prompt: confirm.prompt, reasons: confirm.reasons, meeting: confirm.meeting === true,
    },
  });
}

/** "Yes, call" (Enter or Y): closes the dialog and runs what it was opened for. */
export function confirmRedial(): void {
  const run = redialYes;
  redialYes = null;
  if (!state.redialConfirm) return;
  set({ redialConfirm: null });
  run?.();
}

/** "No" (Esc or N): closes the dialog; the lead stays as it was and nothing dials. */
export function cancelRedial(): void {
  redialYes = null;
  if (state.redialConfirm) set({ redialConfirm: null });
}

/** opts.callFromNumberId: the dial pad's "Call from" pick (SPEC §19, 2026-10-02), for this one call; the server checks
 *  it. Omitted = Automatic (the sticky / best local number). opts.quickCallback: "Call back now" (2026-10-06): redial my
 *  last call to this number from that call's caller ID; pressing C is itself the setter's yes (2026-10-20).
 *  opts.redialConfirmed (PJ 2026-10-20): the setter said Yes to "Call this number again?"; without it a lead whose gate is
 *  confirmable opens that dialog instead of dialing. opts.target (PJ 2026-10-23): 'store' for "Call the store" (A),
 *  'owner' / 'store' for "Call back now" on that number; absent = the default (the owner's number when there is one).
 *  The server works the number out from the lead; the "Call again?" question is about that number's own gate. */
export async function dial(opts: { callFromNumberId?: number | null; quickCallback?: boolean; redialConfirmed?: boolean; meetingConfirmed?: boolean; target?: DialTarget | null } = {}): Promise<void> {
  const item = state.item;
  if (!item || state.lead !== 'ready' || !canDial(state.call)) return;
  const why = phone.whyCantCall();
  if (why) { toast(why, 'bad'); return; }
  const membershipId = item.membership.id;
  const quickCallback = opts.quickCallback === true;
  const redialConfirmed = !quickCallback && opts.redialConfirmed === true;
  // PJ 2026-10-28: the Yes to "Meeting booked. Call anyway?" (it goes with redialConfirmed).
  const meetingConfirmed = redialConfirmed && opts.meetingConfirmed === true;
  const callFromNumberId = quickCallback ? null : opts.callFromNumberId ?? null;
  const target = opts.target ?? null;
  // Called recently: ask first. The press that got here is the question; only the dialog's Yes dials.
  const ask = quickCallback ? null : stillToAsk(confirmableGate(gateFor(item, target)), redialConfirmed, meetingConfirmed);
  if (ask) { askRedial(item, ask, redialAgain(item, opts, ask), e164For(item, target)); return; }
  dispatch({ type: 'DIAL', membershipId });
  if (state.call.phase !== 'preparing') return;
  cancelRequested = false;

  // Calibration (LEADGEN-SPEC §13 CORE 3): the hook the drawer shows for this lead, when there is one.
  const hookId = state.drawer.membershipId === membershipId ? shownHookId(state.drawer.data, state.drawer.live) : null;
  // Opener A/B (REVIEW-2026-10-01): the opener on screen goes with the call and stays on screen for it.
  const openerVariant = currentOpenerLabel(state);
  if (openerVariant) set({ openerPick: { membershipId, label: openerVariant } });
  const extra = {
    ...(hookId !== null ? { hookId } : {}), ...(openerVariant ? { openerVariant } : {}), ...(callFromNumberId ? { callFromNumberId } : {}),
    ...(redialConfirmed ? { redialConfirmed: true } : {}), ...(meetingConfirmed ? { meetingConfirmed: true } : {}), ...(target ? { target } : {}),
  };

  let prep;
  try {
    if (quickCallback) prep = await prepareCallBack(item, extra);
    else if (item.noCampaign) {
      prep = target
        ? await endpoints.prepareNoCampaign(item.business.id, callFromNumberId, redialConfirmed, target)
        : redialConfirmed
          ? await endpoints.prepareNoCampaign(item.business.id, callFromNumberId, true)
          : await endpoints.prepareNoCampaign(item.business.id, callFromNumberId);
    } else prep = Object.keys(extra).length ? await endpoints.prepare(membershipId, extra) : await endpoints.prepare(membershipId);
  } catch (e) {
    const gate = gateFromError(e);
    if (gate) {
      // A refused "Call back now": the button and the call bar show the server's sentence instead of inviting a retry.
      if (quickCallback) noteCallBackRefusal(item, gate, null);
      // The card was stale (the number was called since it loaded) and only soft rules refuse it: the card turns amber
      // and the same dialog asks; its Yes resends with the setter's yes. One press never asks the same question twice;
      // a Yes to "Call this number again?" on a lead booked since the card loaded is asked "Meeting booked" (review
      // 2026-10-28: that yes never passes a meeting).
      const ask = quickCallback ? null : stillToAsk(confirmableGate(gate), redialConfirmed, meetingConfirmed);
      if (ask) {
        dispatch({ type: 'PREPARE_NEEDS_CONFIRM' });
        takeServerGate(item, target, gate);
        askRedial(item, ask, redialAgain(item, opts, ask), e164For(item, target));
        return;
      }
      // A hard refusal (review 2026-10-20): the card takes the server's gate too, so a card that was amber (e.g. another
      // setter placed the 3rd call of the day since it loaded) stops offering "Call again" and Space can't reopen the
      // prompt for a Yes that would only be refused again. A refused C keeps the card as it is (its own sentence shows).
      // PJ 2026-10-23: the gate goes on the number it was about (the store line's for A).
      if (!quickCallback) takeServerGate(item, target, gate);
      dispatch({ type: 'PREPARE_BLOCKED', gate });
      return;
    }
    if (e instanceof ApiFailure && e.body.error === 'number_unavailable') {
      // The lead has no such number any more (cleared as wrong since the card loaded): reload it, say so.
      dispatch({ type: 'PREPARE_FAILED', message: e.body.message });
      void refreshItem(item);
      return;
    }
    if (quickCallback && e instanceof ApiFailure && e.body.error === 'nothing_to_call_back') noteCallBackRefusal(item, null, e.body.message);
    if (e instanceof ApiFailure && e.body.error === 'disposition_required') {
      // The server knows of an unlogged call (e.g. from another tab): bring it up so it gets logged first.
      dispatch({ type: 'PREPARE_FAILED', message: e.body.message });
      set({ pending: 'unchecked' });
      await checkPending();
      return;
    }
    dispatch({ type: 'PREPARE_FAILED', message: `Couldn't start the call: ${errorMessage(e)}` });
    return;
  }
  dispatch({ type: 'PREPARED', callId: prep.callId, to: prep.to, callerId: prep.callerId, now: Date.now() });
  // PJ 2026-10-20: the server offers recording with their yes on this call (all-party consent, setting on).
  set({ lastDialAt: Date.now(), consentRec: prep.consentRecording === true ? { callId: prep.callId, phase: 'offer', error: null } : null });
  if (cancelRequested) {
    // Hang-up pressed while the server was preparing: never connect. The prepared row expires unused (not an attempt).
    cancelRequested = false;
    dispatch({ type: 'CONNECT_FAILED', message: 'Call canceled before it started. Nothing was logged.' });
    return;
  }

  let call: Call;
  try {
    call = await phone.connect({ callId: String(prep.callId), dialToken: prep.dialToken });
  } catch (e) {
    dispatch({ type: 'CONNECT_FAILED', message: errorMessage(e) });
    return;
  }
  const callId = prep.callId;
  const ours = () => state.call.callId === callId;
  call.on('ringing', () => { if (ours()) dispatch({ type: 'RINGING' }); });
  call.on('accept', () => {
    if (!ours()) return;
    dispatch({ type: 'ACCEPTED', now: Date.now() });
    liveOnConnect(); // policy 'on_open_and_connect' only; never blocks the call
  });
  call.on('disconnect', () => { if (ours()) void onEnded(callId); });
  call.on('cancel', () => { if (ours()) void onEnded(callId, 'canceled'); });
  call.on('reject', () => { if (ours()) void onEnded(callId, 'rejected'); });
  call.on('error', (err: PhoneErrorLike) => {
    if (!ours()) return;
    const plain = explainPhoneError(err);
    if (callClosed(call)) void onEnded(callId, 'error', plain.message);
    else toast(plain.message, 'bad');
  });
  // Hang-up pressed while connecting, or an instant failure: the call may already be closed before listeners attached.
  if (callClosed(call)) void onEnded(callId, 'closed');
}

/** The question a dial still has to ask, or null: nothing without a yes yet; with the Yes to "Call this number again?"
 *  only a "Meeting booked" the setter hasn't answered (PJ 2026-10-28); with the Yes to "Meeting booked", nothing. */
export function stillToAsk(confirm: GateConfirm | null, redialConfirmed: boolean, meetingConfirmed: boolean): GateConfirm | null {
  if (!confirm) return null;
  if (!redialConfirmed) return confirm;
  return confirm.meeting === true && !meetingConfirmed ? confirm : null;
}

/** What "Yes, call" does for a dial(): the same dial (the same number), with the setter's yes (and, when the question was
 *  "Meeting booked. Call anyway?", the yes to that), while the same lead is still on screen. */
function redialAgain(item: QueueItem, opts: { callFromNumberId?: number | null; target?: DialTarget | null }, asked: GateConfirm): () => void {
  const key = leadKeyOf(item);
  return () => {
    if (!state.item || leadKeyOf(state.item) !== key) return;
    void dial({
      ...(opts.callFromNumberId ? { callFromNumberId: opts.callFromNumberId } : {}), ...(opts.target ? { target: opts.target } : {}),
      redialConfirmed: true, ...(asked.meeting === true ? { meetingConfirmed: true } : {}),
    });
  };
}

/** A gate prepare answered with becomes the card's for that number (PJ 2026-10-23): the default's is dialGate; the
 *  store line's (or the owner's, when it isn't the default) lives on QueueItem.numbers. */
function takeServerGate(item: QueueItem, target: DialTarget | null, gate: GateResult): void {
  const cur = state.item;
  if (!cur || leadKeyOf(cur) !== leadKeyOf(item)) return;
  const nums = cur.numbers;
  if (!target || !nums || nums.defaultTarget === target) {
    set({ item: { ...cur, dialGate: gate, ...(nums && nums.defaultTarget ? { numbers: withGate(nums, nums.defaultTarget, gate) } : {}) } });
    return;
  }
  set({ item: { ...cur, numbers: withGate(nums, target, gate) } });
}

function withGate(nums: QueueItemNumbers, target: DialTarget, gate: GateResult): QueueItemNumbers {
  const n = target === 'owner' ? nums.owner : nums.store;
  if (!n) return nums;
  return target === 'owner' ? { ...nums, owner: { ...n, gate } } : { ...nums, store: { ...n, gate } };
}

/** The lead on screen loaded again (its numbers changed since the card loaded). Never dials; a failure leaves it. */
async function refreshItem(item: QueueItem): Promise<void> {
  try {
    const fresh = item.noCampaign ? await endpoints.noCampaignItem(item.business.id) : await endpoints.queueItem(item.membership.id);
    const cur = state.item;
    if (cur && leadKeyOf(cur) === leadKeyOf(item) && canDial(state.call)) set({ item: fresh });
  } catch { /* the card stays as it was; prepare decides at the next press */ }
}

// ------------------------------------------------------------------ the owner's line first (PJ 2026-10-23)
//
// PJ: "I want to prioritize owner cells over everything, because that is the direct contact to the decision maker. And if
// they don't pick up, or if we choose to call the store, that should be an option." Dial / Space call the default number
// (the owner's when there is one). A is the other line: on the lead card "Call the store (A)" dials the store line; in the
// wrap-up after an owner's-line call "Call the store now (A)" saves the outcome (No answer with "Owner didn't answer;
// calling the store" when none was picked) and dials the store at once, and after a store-line call "Call the owner now"
// does the same the other way. Each number keeps its own "Call this number again?". S stays Skip.

/** Which of the lead's numbers the call on screen went to ('owner' / 'store'), or null (its one number). */
export function calledTarget(s: DialerState): DialTarget | null {
  return targetOfNumber(s.item, s.call.to);
}

/** The wrap-up's other-line button for the call on screen (null when not shown); why null = ready. */
export function otherLineNow(s: DialerState): ReturnType<typeof otherLineButton> {
  if (s.call.phase !== 'needs_disposition') return null;
  const btn = otherLineButton({
    item: s.item, called: calledTarget(s), code: s.wrap.code, stored: s.call.serverDisposition, saving: s.call.saving, dropping: s.call.dropping,
  });
  if (!btn || btn.why) return btn;
  // A hard block on that number (DNC, hours...) says so; a soft one asks first at the press, as Dial does.
  const g = s.item ? gateFor(s.item, btn.target) : null;
  return g && !g.ok && !g.confirm ? { ...btn, why: g.reasons[0]?.message ?? "That number can't be called right now." } : btn;
}

/** A: the other line. On the lead card (idle / blocked): dial the store line. In the wrap-up: save the call (the outcome
 *  picked, else No answer with the note) and dial the other number at once; only when the outcome stored fits. Nothing
 *  dials without this keypress, and "Call this number again?" still asks for that number when it was called recently. */
export async function callOtherLine(): Promise<void> {
  const s = state;
  if (!s.item) return;
  if (s.call.phase === 'idle' || s.call.phase === 'blocked') {
    if (!storeButton(s.item)) {
      toast('This lead has one number on file: Dial (Space) calls it.', 'info');
      return;
    }
    if (s.lead !== 'ready') return;
    await dial({ target: 'store' });
    return;
  }
  if (s.call.phase !== 'needs_disposition') return;
  const btn = otherLineNow(s);
  if (!btn) { toast('This call went to the lead\'s one number; there is no other line to call.', 'info'); return; }
  if (btn.why) { toast(btn.why, 'warn'); return; }
  const cant = phone.whyCantCall();
  if (cant) { toast(cant, 'bad'); return; } // before saving: never log the call and then fail to dial for this
  const w = s.wrap;
  const callId = s.call.callId;
  const called = calledTarget(s)!;
  const untouched = !w.code || (autoOutcome !== null && autoOutcome.callId === callId && autoOutcome.code === w.code);
  const typed = w.note.trim();
  const marker = otherLineNote(called);
  const note = untouched ? (typed ? `${typed} | ${marker}` : marker) : null;
  if (note !== null) set({ wrap: { ...w, code: 'no_answer', note } });
  const saved = await saveDisposition(s.item.noCampaign ? { saveLead: true } : undefined, { stay: true });
  if (!saved) {
    if (note !== null) {
      const cur = state.wrap;
      set({ wrap: { ...cur, code: cur.code === 'no_answer' ? w.code : cur.code, note: cur.note === note ? w.note : cur.note } });
    }
    return;
  }
  const stored = state.lastSaved?.code ?? null;
  if (!stored || !OTHER_LINE_OUTCOMES.includes(stored)) {
    toast(stored ? `Not dialled: this call is logged as "${DISPOSITION_META[stored].label}".` : 'Not dialled: this call was already logged.', 'info');
    return;
  }
  if (stored === 'wrong_number' && state.item) {
    // The server took the wrong number off the lead, so its other number is now what Dial calls: load the lead again
    // (its numbers and gates changed) and dial its default.
    await refreshItem(state.item);
    await dial();
    return;
  }
  if (state.item && numberOf(state.item, btn.target)) await dial({ target: btn.target });
}

const wait = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

/** The server logged `vm_dropped` for the call on screen: nothing is owed, so show it and load the next lead. */
async function afterDropLogged(business: string): Promise<void> {
  set({ lastSaved: { code: 'vm_dropped', business, at: Date.now() }, bookedMeeting: null });
  void refreshToday();
  await loadNext();
}

async function onEnded(callId: number, reason?: string, error?: string): Promise<void> {
  if (state.call.callId !== callId || !isLive(state.call)) return;
  dispatch({ type: 'ENDED', now: Date.now(), reason, error });
  // A drop ends the browser leg (Twilio -> /twilio/dial-action -> <Hangup/>) before the Worker has logged vm_dropped
  // and answered /drop. Asking /pending now would race that write, so wait for the drop's answer first.
  const drop = dropInFlight;
  if (drop && drop.callId === callId) await Promise.race([drop.done, wait(DROP_WAIT_MS)]);
  if (state.call.callId !== callId || state.call.phase !== 'ended') return;
  let serverPending: boolean | null;
  try {
    const p = await endpoints.pending();
    serverPending = p !== null && p.id === callId;
  } catch {
    serverPending = null; // can't tell: fall back to "did Twilio place it"
  }
  if (state.call.callId !== callId || state.call.phase !== 'ended') return;
  const ended = state.call;
  // Still dropping (the wait ran out) and the server owes nothing: the drop's own vm_dropped write is what cleared it.
  const dropped = ended.serverDisposition === 'vm_dropped' || (ended.dropping && serverPending === false);
  dispatch({ type: 'PENDING_CHECKED', serverPending });
  const business = state.item?.business.name ?? '';
  if (getDialer().call.phase === 'needs_disposition') {
    const w = state.wrap;
    if (!w.code) {
      const code = defaultOutcome({ meetingBooked: !!state.bookedMeeting, vmLiveRead: w.vmLiveRead, answered: state.call.answered });
      if (code) set({ wrap: { ...w, code } });
      autoOutcome = code === 'no_answer' ? { callId, code } : null;
    }
    void loadCallBack(callId); // "Call back now": may this number be redialled right away? (the button says why not)
  } else if (dropped) {
    await afterDropLogged(business);
  } else if (ended.placed && serverPending === false) {
    // Their line rang, yet nothing is owed: the outcome was already saved (another tab, or an admin). Move on.
    toast(`Your call to ${business || 'this lead'} is already logged. Loading the next lead.`, 'info');
    void refreshToday();
    await loadNext();
  } else if (!state.call.error) {
    set({ call: { ...state.call, error: 'The call ended before it reached their phone. Nothing was logged; you can dial again.' } });
  }
}

export function hangup(): void {
  if (state.call.phase === 'preparing') { cancelRequested = true; return; }
  if (!isLive(state.call)) return;
  const callId = state.call.callId;
  phone.hangup();
  // Safety net (SPEC §7.3): if the browser leg doesn't report the hang-up quickly, end both legs server-side.
  setTimeout(() => {
    if (!isLive(state.call) || state.call.callId !== callId) return;
    if (callId !== null) {
      endpoints.hangup(callId).catch((e) => toast(`Hang-up didn't confirm: ${errorMessage(e)}`, 'bad'));
      phone.forceEnd();
      void onEnded(callId, 'forced');
    }
  }, 4000);
}

export function toggleMute(): void {
  if (state.call.phase === 'in_call' || state.call.phase === 'ringing' || state.call.phase === 'connecting') phone.toggleMute();
}

export function sendDigits(d: string): void {
  if (isLive(state.call)) phone.sendDigits(d);
}

export function dismissBlock(): void { dispatch({ type: 'DISMISS_BLOCK' }); }
export function clearCallError(): void { dispatch({ type: 'CLEAR_ERROR' }); }

// ------------------------------------------------------------------ recording with their yes (PJ 2026-10-20)

function setConsentRec(callId: number, phase: ConsentRecPhase, error: string | null = null): void {
  const cur = state.consentRec;
  if (!cur || cur.callId !== callId) return;            // another call is up now: nothing to update
  set({ consentRec: { callId, phase, error } });
}

/** "Record (they said yes)": opens the confirm (ConsentRecordConfirm). Nothing is recorded until its yes. */
export function askConsentRecording(): void {
  const cur = state.consentRec;
  if (!cur || state.call.phase !== 'in_call' || state.call.callId !== cur.callId || cur.phase !== 'offer') return;
  setConsentRec(cur.callId, 'confirm');
}

/** "Cancel" (or Esc): the confirm closes and nothing is recorded. */
export function cancelConsentRecording(): void {
  const cur = state.consentRec;
  if (cur && cur.phase === 'confirm') setConsentRec(cur.callId, 'offer');
}

/** "They said yes, start": POST /api/calls/:id/record-consent. A refusal or a Twilio problem is one line under the call
 *  bar; the call goes on either way. */
export async function startConsentRecording(): Promise<void> {
  const cur = state.consentRec;
  if (!cur || cur.phase !== 'confirm' || state.call.phase !== 'in_call' || state.call.callId !== cur.callId) return;
  const callId = cur.callId;
  setConsentRec(callId, 'starting');
  try {
    await endpoints.recordConsent(callId);
    setConsentRec(callId, 'on');
    toast(`Recording's on. Say it out loud: "${CONSENT_RECORDING_ON}"`, 'ok');
  } catch (e) {
    const code = e instanceof ApiFailure ? e.body.error : null;
    setConsentRec(callId, phaseAfterRefusal(code), `Not recording: ${errorMessage(e)}`);
  }
}

/** "Stop recording": they took the yes back. What was recorded with it is kept. */
export async function stopConsentRecording(): Promise<void> {
  const cur = state.consentRec;
  if (!cur || cur.phase !== 'on' || state.call.callId !== cur.callId) return;
  const callId = cur.callId;
  setConsentRec(callId, 'stopping');
  try {
    await endpoints.stopConsentRecording(callId);
    setConsentRec(callId, 'stopped');
    toast('Recording stopped. Nothing more is recorded on this call.', 'ok');
  } catch (e) {
    setConsentRec(callId, 'on', `Couldn't stop the recording: ${errorMessage(e)}`);
  }
}

/** The OK on the recording line under the call bar. */
export function dismissConsentRecError(): void {
  const cur = state.consentRec;
  if (cur?.error) set({ consentRec: { ...cur, error: null } });
}

// ------------------------------------------------------------------ voicemail drop

/** Why Drop VM is unavailable right now (first reason), or null when it may be pressed. A line type that was never
 *  checked, came back 'unknown' or is over 14 days old doesn't disable it: the server checks the line when V is pressed
 *  (PJ 2026-10-03). Known blockers (a cell, Canada, CA/WA/TX, toll-free, the cap, no recording) still do. */
export function dropBlockReason(s: DialerState, userId: number): string | null {
  const item = s.item;
  if (!item) return 'No lead on screen.';
  // PJ 2026-10-23: the drop gate of the number this call went to (a call to the owner's line never takes a drop).
  const called = calledTarget(s);
  const own = called && item.numbers?.defaultTarget !== called ? numberOf(item, called)?.dropGate : null;
  const serverReasons = (own ?? dropGateBeforeLineCheck(item.dropGate, item.business, { lookupPossible: !called || !isOtherThanMain(item, s.call.to) }))
    .reasons.filter((r) => r.code !== 'drop_not_answered');
  if (serverReasons.length) return serverReasons[0].message;
  if (s.call.phase !== 'in_call') return 'Voicemail drops work only once the call is answered by their voicemail.';
  if (s.call.serverDisposition) return 'This call already has an outcome saved.';
  if (s.recordings === null) return 'Loading your recordings…';
  const rec = pickDropRecording(s.recordings, { userId, campaignId: item.campaign.id, vmCount: item.membership.vmCount });
  if (!rec) return s.recordingsError ?? 'You have no approved voicemail recording for this campaign (Voicemails page).';
  return null;
}

/** The call's number is not the business's own phone_e164 (the owner's cell, a Google line): never looked up. */
function isOtherThanMain(item: QueueItem, to: string | null): boolean {
  return !!to && to !== item.business.phoneE164;
}

export async function dropVoicemail(userId: number): Promise<void> {
  const s = state;
  const why = dropBlockReason(s, userId);
  if (why) { toast(why, 'warn'); return; }
  const item = s.item!;
  const callId = s.call.callId!;
  const rec = pickDropRecording(s.recordings!, { userId, campaignId: item.campaign.id, vmCount: item.membership.vmCount })!;
  dispatch({ type: 'DROP_STARTED' });
  if (!state.call.dropping) return;
  let finish: () => void = () => {};
  const entry = { callId, done: new Promise<void>((resolve) => { finish = resolve; }) };
  dropInFlight = entry;
  try {
    await endpoints.drop(callId, rec.id);
    const before = state.call;
    dispatch({ type: 'DROP_FINISHED', ok: true });
    const after = state.call;
    if (before.callId === callId && before.phase === 'needs_disposition' && after.phase === 'idle') {
      // The wrap-up had been forced while the drop was confirming; the server logged vm_dropped, so move on.
      toast('Voicemail dropped and logged. Loading the next lead.', 'ok');
      await afterDropLogged(item.business.name);
    } else if (isLive(after)) {
      toast('Voicemail dropped. The next lead loads when the call ends.', 'ok');
    } else {
      toast('Voicemail dropped and logged.', 'ok');
    }
  } catch (e) {
    const gate = gateFromError(e);
    const message = gate ? `Drop blocked: ${gate.reasons.map((r) => r.message).join(' ')}` : `Drop failed: ${errorMessage(e)}`;
    // The call stays up (DROP_FINISHED ok:false keeps it live) so the setter can read the voicemail live. The refusal
    // becomes the lead's drop gate, and a line type the server just looked up (e.g. it's a cell) replaces the stale one,
    // so Drop VM now shows the real reason instead of inviting another press.
    if (gate) {
      const cur = state.item;
      if (cur && cur.membership.id === item.membership.id && cur.business.id === item.business.id) {
        const check = (gate as DropRefusal).lineCheck;
        set({ item: { ...cur, dropGate: gate, business: check ? { ...cur.business, lineType: check.lineType, lookupAt: check.lookupAt } : cur.business } });
      }
    }
    dispatch({ type: 'DROP_FINISHED', ok: false, message });
    toast(message, 'bad');
  } finally {
    if (dropInFlight === entry) dropInFlight = null;
    finish();
  }
}

// ------------------------------------------------------------------ wrap-up and disposition

export function updateWrap(patch: Partial<WrapUp>): void {
  if (patch.code !== undefined) autoOutcome = null;
  set({ wrap: { ...state.wrap, ...patch } });
  // A refused save ("that callback time doesn't work…") is stale once the setter picks another time or outcome.
  if (patch.callbackAt !== undefined || patch.code !== undefined) dispatch({ type: 'CLEAR_SAVE_ERROR' });
}

/** The business on screen changed (an observation verified in the drawer, or withdrawn by a live check). */
export function replaceBusiness(b: Business): void {
  const item = state.item;
  if (!item || item.business.id !== b.id) return;
  set({ item: { ...item, business: b } });
}

export function chooseDisposition(code: DispositionCode): void {
  if (code === 'vm_dropped') return;
  autoOutcome = null; // the setter picked it (even when it is the No answer that was pre-selected)
  const w = state.wrap;
  set({ wrap: { ...w, code, spokeToDm: null, callbackAt: DISPOSITION_META[code].needsCallback ? w.callbackAt : null } });
  dispatch({ type: 'CLEAR_SAVE_ERROR' });
}

/** Marks that the live voicemail card was used: the outcome defaults to "Left voicemail (live)". */
export function markLiveVoicemail(): void {
  if (!state.item) return;
  const w = state.wrap;
  set({ wrap: { ...w, vmLiveRead: true, code: w.code ?? (state.call.phase === 'needs_disposition' ? 'vm_live' : w.code) } });
}

/** What stops this wrap-up from being saved, in one sentence; null = ready. */
export function wrapProblem(s: DialerState, now = new Date()): string | null {
  return wrapProblemFor(s.wrap, { meetingBooked: !!s.bookedMeeting || s.call.serverDisposition === 'meeting_booked' }, now);
}

export function buildDisposition(s: DialerState): DispositionRequest {
  return buildRequest(s.wrap, s.item?.business);
}

export const NO_CAMPAIGN_CHOICE = 'This call had no campaign: press Save to My leads or Don\'t save.';

/**
 * Saves the wrap-up and loads the next lead (never dials it). Resolves true when the outcome is settled and the next lead
 * was asked for, false when nothing moved on (not saved, or nothing was owed). A no-campaign call (SPEC §19, 2026-10-02)
 * needs choice.saveLead: true = Save to My leads, false = Don't save; then it goes back to the campaign session or picker.
 * opts.stay ("Call back now", 2026-10-06): the lead stays on screen for the redial instead (true = settled).
 */
export async function saveDisposition(choice?: { saveLead: boolean }, opts: { stay?: boolean } = {}): Promise<boolean> {
  const s = state;
  if (s.call.phase !== 'needs_disposition' || s.call.saving || s.call.callId === null) return false;
  const noCampaign = !!s.item?.noCampaign;
  if (noCampaign && !choice) { dispatch({ type: 'SAVE_FAILED', message: NO_CAMPAIGN_CHOICE }); return false; }
  const problem = wrapProblem(s) ?? (noCampaign && choice ? noCampaignProblem(s.wrap, choice.saveLead) : null);
  if (problem) { dispatch({ type: 'SAVE_FAILED', message: problem }); return false; }
  const req: DispositionRequest = noCampaign && choice ? { ...buildDisposition(s), saveLead: choice.saveLead } : buildDisposition(s);
  const callId = s.call.callId;
  const business = s.item?.business.name ?? '';
  dispatch({ type: 'SAVE_STARTED' });
  if (!state.call.saving) return false;
  let savedCode: DispositionCode | null = req.code;
  try {
    await endpoints.dispose(callId, req);
  } catch (e) {
    const settled = e instanceof ApiFailure ? settledOutcome(e.status, e.body.error) : null;
    if (state.call.serverDisposition === 'meeting_booked' && e instanceof ApiFailure && e.status < 500) {
      // A meeting booked on this call already stored `meeting_booked`; if adding the note fails, the outcome is still saved.
      toast(`The meeting is saved, but the extra note wasn't: ${e.body.message}`, 'warn');
      savedCode = 'meeting_booked';
    } else if (settled?.kind === 'nothing') {
      // 409 not_placed: the call never reached their line. Nothing to log; the same lead can be dialed again.
      dispatch({ type: 'NOTHING_OWED', message: errorMessage(e) });
      set({ wrap: { ...emptyWrap(state.item), note: state.wrap.note }, bookedMeeting: null });
      return false;
    } else if (settled?.kind === 'logged') {
      // The call already has a final outcome (voicemail drop, DNC, meeting, or saved > 10 min ago): nothing is owed.
      toast(`Already logged: ${errorMessage(e)}${opts.stay ? '' : ' Loading the next lead.'}`, 'info');
      savedCode = settled.savedCode;
    } else if (e instanceof ApiFailure && callbackRefusal(e.status, e.body.error, e.body.details)) {
      // 400 callback_not_allowed: the gate wouldn't allow a call at that time. Keep the wrap-up open, show the server's
      // sentence, and offer its earliest allowed time as the first callback preset (one click, then Enter).
      const refusal = callbackRefusal(e.status, e.body.error, e.body.details)!;
      set({ wrap: { ...state.wrap, callbackEarliestAt: refusal.earliestAt } });
      dispatch({ type: 'SAVE_FAILED', message: `Not saved: ${errorMessage(e)}` });
      return false;
    } else {
      dispatch({ type: 'SAVE_FAILED', message: `Not saved: ${errorMessage(e)}` });
      return false;
    }
  }
  // Force the wrap-up closed even if a late drop already moved the machine to idle (SAVED is then a no-op).
  dispatch({ type: 'SAVED' });
  set({ lastSaved: savedCode ? { code: savedCode, business, at: Date.now() } : null, bookedMeeting: null });
  autoOutcome = null;
  void refreshToday();
  if (opts.stay) {
    // "Call back now": same lead, same contact details; the redial's wrap-up starts with no outcome, note or callback.
    const w = state.wrap;
    set({ wrap: { ...w, code: null, spokeToDm: null, objection: '', note: '', callbackAt: null, vmLiveRead: false, callbackEarliestAt: null } });
    return true;
  }
  if (noCampaign) {
    toast(choice?.saveLead
      ? `Saved to My leads: ${business || 'this number'}.`
      : `Not saved: ${business || 'this number'} is hidden from your lists. The call and its outcome are kept.`, 'ok');
    await leaveNoCampaign();
    return true;
  }
  await loadNext();
  return true;
}

/**
 * Enter on the wrap-up (REVIEW-2026-10-01 §4.1): save the outcome, load the next lead and dial it, all from this one
 * keypress. It dials only a different lead that this save loaded, only when its gate is open and the phone can call,
 * and only once: a blocked or failed dial stays on screen like any other. Nothing here ever dials without a keypress.
 */
export async function saveAndDialNext(): Promise<void> {
  if (state.item?.noCampaign) { await saveDisposition(); return; } // asks for Save to My leads / Don't save; never dials
  const before = state.item?.membership.id ?? null;
  if (!(await saveDisposition())) return;
  const s = state;
  if (s.lead !== 'ready' || !s.item || s.item.membership.id === before || !s.item.dialGate.ok || !canDial(s.call)) return;
  await dial();
}

// ------------------------------------------------------------------ "Call back now" (C)
//
// PJ 2026-10-06: "Let's say I just called a place but it hung up or something. I should be able to call back or log it.
// If I log it I get onto the next lead, but there should be an option to just call them back right away too."
// In the wrap-up C saves the call (the outcome picked, else No answer with a note) and redials the same number from the
// same caller ID, only when the saved outcome is a hang-up, voicemail or gatekeeper one; on the lead card it redials a
// call already saved, while the 15 minutes last. The server decides (POST /api/calls/prepare {quickCallback}, the same
// outcome rule); GET /api/calls/:id/call-back and QueueItem.callBack say so in advance. Enter (save and dial the next
// lead) is untouched. Since PJ 2026-10-20 pressing C is itself the setter's yes: no "Call this number again?" dialog, and
// it passes every soft attempt rule up to the hard 3-a-day cap (the old once-per-number-per-day limit is gone).

/** Why "Call back now" can't be pressed: the outcome, a save or voicemail drop in flight, the 15 minutes, the check or
 *  the gate. */
export type CallBackKind = 'ready' | 'checking' | 'outcome' | 'saving' | 'dropping' | 'refused' | 'expired';

/** The "Call back now" button for the screen right now, or null when it isn't shown: in the wrap-up of a call that
 *  reached their line, and on the lead card (idle / blocked) while the 15 minutes last. why null = ready to press. */
export function callBackButton(s: DialerState, now: number): { why: string | null; kind: CallBackKind } | null {
  const item = s.item;
  const cb = s.callBack;
  if (!item || !cb || cb.leadKey !== leadKeyOf(item)) return null;
  const owed = s.call.phase === 'needs_disposition';
  if (owed ? s.call.callId !== cb.callId : s.call.phase !== 'idle' && s.call.phase !== 'blocked') return null;
  const expired = now > cb.until;
  if (!owed && (expired || s.lead !== 'ready')) return null;
  if (owed) {
    // An outcome the server already stored (a meeting booked on the call, a voicemail drop) is the call's outcome.
    const stored = s.call.serverDisposition;
    const outcome = stored ? callBackLoggedProblem(stored) ?? callBackOutcomeProblem(s.wrap.code) : callBackOutcomeProblem(s.wrap.code);
    if (outcome) return { why: outcome, kind: 'outcome' };
    if (s.call.saving) return { why: 'Saving…', kind: 'saving' };
    // A drop still confirming may already have logged the call as a voicemail drop, which is never called back.
    if (s.call.dropping) return { why: 'Still confirming the voicemail drop; a dropped voicemail is not called back.', kind: 'dropping' };
  }
  if (expired) return { why: 'More than 15 minutes have passed since this call, so Call back now is off; Call again (Space) asks you first, then dials.', kind: 'expired' };
  if (cb.error) return { why: cb.error, kind: 'refused' };
  if (!cb.gate) return { why: 'Checking the calling rules…', kind: 'checking' };
  if (!cb.gate.ok) return { why: cb.gate.reasons[0]?.message ?? "This number can't be called back right now.", kind: 'refused' };
  return { why: null, kind: 'ready' };
}

/** The amber "Can't call back now: …" line under the call bar, or null: a refusal by the calling rules (or the 15
 *  minutes running out in the wrap-up), and only once C was pressed or the server refused at press time. Before that
 *  the reason is the disabled button's hover text only. */
export function callBackNotice(s: DialerState, now: number): string | null {
  const cb = callBackButton(s, now);
  if (!cb || !s.callBack?.pressed) return null;
  return cb.kind === 'refused' || (cb.kind === 'expired' && s.call.phase === 'needs_disposition') ? cb.why : null;
}

/** The button's hover text when it can be pressed. */
export function callBackTitle(s: DialerState): string {
  const from = s.callBack?.callerId ? ` from ${fmtPhone(s.callBack.callerId)}` : '';
  const what = s.call.phase === 'needs_disposition' ? 'Logs this call (No answer if you picked nothing) and redials' : 'Redials';
  const number = s.item ? fmtPhone(e164For(s.item, s.callBack?.target ?? null)) : '';
  return `${what} ${number || 'this number'} right away${from}, the same caller ID, within 15 minutes of the call (never more than 3 calls a day to one number).`;
}

/** C / the button: in the wrap-up, save the call (the outcome picked, else No answer + the note) then redial; on the
 *  lead card, redial. Nothing happens without this keypress, and a refusal shows the server's sentence. Refused while a
 *  voicemail drop is still confirming (callBackButton), and after the save it redials only when the outcome stored is
 *  a hang-up, voicemail or gatekeeper one (the server, e.g., may answer that the call was already logged otherwise). */
export async function callBackNow(): Promise<void> {
  const s = state;
  const btn = callBackButton(s, Date.now());
  if (!btn) {
    if (s.item && (canDial(s.call) || s.call.phase === 'needs_disposition')) {
      toast('Nothing to call back: Call back now works within 15 minutes of your own call to this lead.', 'info');
    }
    return;
  }
  if (btn.why) { markCallBackPressed(); toast(btn.why, 'warn'); return; }
  const cant = phone.whyCantCall();
  if (cant) { toast(cant, 'bad'); return; } // before saving: never log the call and then fail to redial for this
  if (s.call.phase === 'needs_disposition') {
    if (s.call.dropping) return; // callBackButton says so; never save over a drop that may already be logged
    const w = s.wrap;
    const callId = s.call.callId;
    const untouched = !w.code || (autoOutcome !== null && autoOutcome.callId === callId && autoOutcome.code === w.code);
    const typed = w.note.trim();
    const note = untouched ? (typed ? `${typed} | ${CALL_BACK_NOW_NOTE}` : CALL_BACK_NOW_NOTE) : null;
    if (note !== null) set({ wrap: { ...w, code: 'no_answer', note } });
    // A no-campaign call is kept in My leads (the redial's own wrap-up asks again, and its answer wins).
    const saved = await saveDisposition(s.item?.noCampaign ? { saveLead: true } : undefined, { stay: true });
    if (!saved) {
      // Not saved (or nothing was owed): take back what C wrote, so the wrap-up reads as the setter left it.
      if (note !== null) {
        const cur = state.wrap;
        set({ wrap: { ...cur, code: cur.code === 'no_answer' ? w.code : cur.code, note: cur.note === note ? w.note : cur.note } });
      }
      return;
    }
    // Redial only when what is stored now fits: the server may say the call was already logged (a voicemail drop that
    // went through, a meeting, an outcome saved over 10 minutes ago).
    const stored = state.lastSaved?.code ?? null;
    const item = state.item;
    if (!stored) {
      toast('Not redialled: this call was already logged. The Call back button shows whether it can still be called back.', 'info');
      if (callId !== null) void loadCallBack(callId);
      return;
    }
    const logged = callBackLoggedProblem(stored);
    if (logged) {
      if (item) noteCallBackRefusal(item, null, logged);
      toast(`Not redialled: ${logged}`, 'warn');
      return;
    }
  }
  // PJ 2026-10-23: the same number the call went to (the owner's line or the store line).
  const target = state.callBack?.target ?? null;
  await dial({ quickCallback: true, ...(target ? { target } : {}) });
}

/** C was pressed while the button is off: its reason now shows under the call bar too. */
function markCallBackPressed(): void {
  const cb = state.callBack;
  if (cb && !cb.pressed) set({ callBack: { ...cb, pressed: true } });
}

/** The wrap-up's check: may this call's number be redialled right now? */
async function loadCallBack(callId: number): Promise<void> {
  const item = state.item;
  if (!item) return;
  const leadKey = leadKeyOf(item);
  const until = (state.call.endedAt ?? Date.now()) + CALL_BACK_WINDOW_MS; // until the server answers
  set({ callBack: { leadKey, callId, until, callerId: state.call.callerId, gate: null, error: null, pressed: false, target: calledTarget(state) } });
  const mine = () => state.callBack?.callId === callId && state.callBack.leadKey === leadKey;
  try {
    const offer = await api.get<CallBackOffer>(`/api/calls/${callId}/call-back`);
    if (mine()) set({ callBack: callBackFromOffer(leadKey, offer) });
  } catch (e) {
    if (!mine()) return;
    const message = e instanceof ApiFailure && e.status < 500 ? e.body.message : `Couldn't check the calling rules for Call back now: ${errorMessage(e)}`;
    set({ callBack: { ...state.callBack!, error: message } });
  }
}

/** POST /api/calls/prepare for "Call back now": the same route as endpoints.prepare / prepareNoCampaign, plus
 *  quickCallback. The server picks the caller ID (the called-back call's own), so no "Call from" is sent. */
function prepareCallBack(item: QueueItem, extra: { hookId?: number; openerVariant?: string; target?: DialTarget }): Promise<CallPrepareResponse> {
  const target = extra.target ? { target: extra.target } : {};
  if (item.noCampaign) return api.post<CallPrepareResponse>('/api/calls/prepare', { noCampaign: true, businessId: item.business.id, quickCallback: true, ...target });
  const body: CallPrepareRequest = {
    membershipId: item.membership.id, quickCallback: true, ...target,
    ...(extra.hookId !== undefined ? { hookId: extra.hookId } : {}), ...(extra.openerVariant ? { openerVariant: extra.openerVariant } : {}),
  };
  return api.post<CallPrepareResponse>('/api/calls/prepare', body);
}

/** A refused "Call back now" becomes the lead's call-back state, so the button shows why instead of inviting a retry;
 *  it came back from a press, so the call bar spells it out too. */
function noteCallBackRefusal(item: QueueItem, gate: GateResult | null, message: string | null): void {
  const cb = state.callBack;
  if (!cb || cb.leadKey !== leadKeyOf(item) || !state.item || leadKeyOf(state.item) !== cb.leadKey) return;
  set({ callBack: { ...cb, ...(gate ? { gate } : {}), ...(message ? { error: message } : {}), pressed: true } });
}

/** The label of the opener on screen for the lead on screen (null without a script), as the Script panel shows it. */
export function currentOpenerLabel(s: DialerState): string | null {
  const item = s.item;
  const openers = s.campaign?.script?.openers;
  if (!item || !openers?.length || s.campaign?.id !== item.campaign.id) return null;
  const chosen = s.openerPick && s.openerPick.membershipId === item.membership.id ? s.openerPick.label : null;
  return shownOpener(openers, { hasObservation: !!verifiedObservation(item.business), membershipId: item.membership.id, chosen }).opener?.label ?? null;
}

/** The A/B selector: pick another opener for the lead on screen. Locked once the call is dialled (it went with the call). */
export function chooseOpener(label: string): void {
  const item = state.item;
  if (!item || !canDial(state.call)) return;
  set({ openerPick: { membershipId: item.membership.id, label } });
}

/** After a booking in the Book panel (SPEC §15.1: toast → auto-disposition meeting_booked). via 'link' (PJ 2026-10-20):
 *  the booking they made on the Calendly link, linked to this lead; the server logged the call itself when it could
 *  (`disposition`), like /book. A no-campaign pad call is saved to My leads: a meeting keeps the number. */
export async function onMeetingBooked(meeting: Meeting & { disposition?: unknown }, via: 'calendly' | 'manual' | 'link'): Promise<void> {
  set({ bookedMeeting: meeting, wrap: { ...state.wrap, code: 'meeting_booked', spokeToDm: true } });
  const callId = state.call.callId;
  if (callId === null) return;
  const pad = !!state.item?.noCampaign;
  if (state.call.phase === 'needs_disposition') { await saveDisposition(pad ? { saveLead: true } : undefined); return; }
  if (via === 'calendly' || (via === 'link' && meeting.disposition)) { dispatch({ type: 'SERVER_DISPOSED', code: 'meeting_booked' }); return; }
  try {
    await endpoints.dispose(callId, { code: 'meeting_booked', spokeToDm: true, ...(pad ? { saveLead: true } : {}) });
    dispatch({ type: 'SERVER_DISPOSED', code: 'meeting_booked' });
  } catch (e) {
    toast(`Meeting saved; log "Meeting booked" after the call (${errorMessage(e)}).`, 'warn');
  }
}

// ------------------------------------------------------------------ Business drawer (LEADGEN-SPEC §11)
//
// - Prefetch: GET drawer fires as soon as a lead is on screen (syncDrawer, from set()); I then renders from memory.
// - Live checks: the free site check on appear when the primary hook depends on the site; Places once on open (and on
//   answer with 'on_open_and_connect'); R re-runs them. The server enforces the caps; nothing here stores live data.
// - Non-modal: none of this touches the call machine, the wrap-up or the hotkeys.

let drawerSeq = 0;          // bumps whenever the lead on screen changes; late answers for an old lead are ignored
let researchTimer: ReturnType<typeof setTimeout> | null = null;
/** How often, and how many times, the drawer re-reads itself while a one-lead research runs (§4.4: 30-90 s). */
export const RESEARCH_POLL_MS = 20_000;
export const RESEARCH_POLL_MAX = 9;
const RESEARCH_RUNNING: SearchStatus[] = ['draft', 'estimated', 'running'];

function patchDrawer(patch: Partial<DrawerUi>): void {
  set({ drawer: { ...state.drawer, ...patch } });
}

function stopResearchPoll(): void {
  if (researchTimer) clearTimeout(researchTimer);
  researchTimer = null;
}

function syncDrawer(): void {
  const item = state.item;
  const d = state.drawer;
  if (item ? d.membershipId === item.membership.id && d.businessId === item.business.id : d.businessId === null) return;
  if (d.businessId !== null) dropLive(d.businessId);
  stopResearchPoll();
  const mine = ++drawerSeq;
  if (!item) { set({ drawer: NO_DRAWER }); return; }
  const businessId = item.business.id;
  const membershipId = item.membership.id;
  set({ drawer: { ...NO_DRAWER, businessId, membershipId, data: cachedDrawer(businessId), loading: true } });
  void loadDrawer(mine, businessId, membershipId);
}

function preloadImage(url: string | null | undefined): void {
  if (!url || typeof Image === 'undefined') return;
  try { new Image().src = url; } catch { /* a preload is a nicety */ }
}

async function loadDrawer(mine: number, businessId: number, membershipId: number): Promise<void> {
  let data: DrawerData;
  try {
    data = await prefetchDrawer(businessId, membershipId);
  } catch (e) {
    if (mine === drawerSeq) patchDrawer({ loading: false, error: `Couldn't load business info: ${errorMessage(e)}` });
    return;
  }
  if (mine !== drawerSeq) return;
  preloadImage(data.website?.screenshotUrl);
  patchDrawer({ data, loading: false, error: null });
  // Opened before the data landed: the on-open check (it includes the site when the hook needs it). Otherwise, or when
  // Places isn't available, the free site check the hook depends on.
  const onOpen = state.drawer.open ? partsOnOpen(data, state.drawer.placesTried) : [];
  const parts = onOpen.length ? onOpen : partsOnAppear(data);
  if (parts.length) void runLive(mine, parts);
}

async function runLive(mine: number, parts: LivePart[]): Promise<void> {
  const d = state.drawer;
  if (!parts.length || d.businessId === null || !d.data || mine !== drawerSeq) return;
  const businessId = d.businessId;
  const hookId = d.data.hook.primary?.id;
  patchDrawer({ liveInFlight: d.liveInFlight + 1, liveError: null, ...(parts.includes('places') ? { placesTried: true } : {}) });
  let error: string | null = null;
  try {
    await runLiveCheck(businessId, { parts, ...(hookId !== undefined ? { hookId } : {}), membershipId: d.membershipId ?? undefined });
  } catch (e) {
    error = `Live check didn't run: ${errorMessage(e)}`;
  }
  if (mine !== drawerSeq) return;
  const live = cachedLive(businessId);
  patchDrawer({ live, liveInFlight: Math.max(0, state.drawer.liveInFlight - 1), liveError: error });
  afterLive(live);
}

/** A live result that takes the verified observation back, or says the business is closed. Nothing is written. */
function afterLive(live: LiveCheck | null): void {
  const d = state.drawer;
  const item = state.item;
  if (!live || !d.data || !item || item.business.id !== d.businessId) return;
  const hv = hookView(d.data, live);
  const b = item.business;
  if (hv.withdrawn && b.observationVerifiedAt && sameSentence(b.observation, hv.withdrawn.sayText)) {
    // The Script panel would open with a sentence the live check just contradicted: hide it for this lead (page memory
    // only; the server copy is untouched and expires on its own).
    replaceBusiness({ ...b, observationVerifiedAt: null });
    toast(`The opener fact no longer holds (${hv.withdrawnReason ?? 'live check'}). Observation openers are hidden for this call.`, 'warn');
  }
  if (live.banner && !d.open) toast(live.banner, 'bad');
}

function liveOnOpen(): void {
  const d = state.drawer;
  const parts = partsOnOpen(d.data, d.placesTried);
  if (parts.length) void runLive(drawerSeq, parts);
}

/** Call answered: Places when the policy is 'on_open_and_connect' (once per lead). */
export function liveOnConnect(): void {
  const d = state.drawer;
  const parts = partsOnConnect(d.data, d.placesTried);
  if (parts.length) void runLive(drawerSeq, parts);
}

export function openDrawer(): void {
  const item = state.item;
  if (!item) return;
  if (state.drawer.membershipId !== item.membership.id) syncDrawer();
  const d = state.drawer;
  patchDrawer({ open: true });
  if (!d.data && !d.loading && d.businessId !== null) {
    // The prefetch failed: opening again is the retry.
    patchDrawer({ loading: true, error: null });
    void loadDrawer(drawerSeq, d.businessId, item.membership.id);
    return;
  }
  if (d.data) liveOnOpen();
}

export function closeDrawer(): void {
  if (state.drawer.open) patchDrawer({ open: false });
}

/** I. */
export function toggleDrawer(): void {
  if (state.drawer.open) closeDrawer(); else openDrawer();
}

/** R: re-run the live check. Only while the drawer is open (the hotkey map enforces it too). */
export function recheckLive(): void {
  const d = state.drawer;
  if (!d.open || !d.data) return;
  const parts = partsOnRecheck(d.data);
  if (!parts.length) { toast('Nothing to check live for this business: no Google place ID and no website.', 'info'); return; }
  void runLive(drawerSeq, parts);
}

/** Verify (a click, never a key): the hook's sentence and evidence become the business's verified observation. */
export async function verifyHook(hook: DrawerHook): Promise<void> {
  const d = state.drawer;
  const item = state.item;
  if (!item || !d.data || d.businessId !== item.business.id || d.verifying) return;
  const mine = drawerSeq;
  patchDrawer({ verifying: true });
  try {
    const b = await verifyObservation(item.business.id, { observation: hook.sayText, observationSourceUrl: hook.evidenceUrl });
    if (mine !== drawerSeq) return;
    const now = new Date().toISOString();
    const cur = state.item?.business;
    if (b && b.id === item.business.id) replaceBusiness(b);
    else if (cur) replaceBusiness({ ...cur, observation: hook.sayText, observationSourceUrl: hook.evidenceUrl, observationVerifiedAt: now });
    const data = state.drawer.data!;
    const block: DrawerHookBlock = { ...data.hook, observationVerifiedAt: b?.observationVerifiedAt ?? now, verifiedHookId: hook.id };
    const next: DrawerData = { ...data, hook: block };
    putDrawer(item.business.id, next);
    patchDrawer({ data: next, verifying: false });
    toast('Verified. The Script panel can now open with it.', 'ok');
  } catch (e) {
    if (mine === drawerSeq) patchDrawer({ verifying: false });
    toast(`Not verified: ${errorMessage(e)}`, 'bad');
  }
}

/** "Research this business (about $0.03)" for a lead with no snapshot (§4.4); the drawer re-reads itself while it runs. */
export async function researchBusiness(): Promise<void> {
  const d = state.drawer;
  if (d.businessId === null || !d.data || !d.data.research.available) return;
  const mine = drawerSeq;
  const businessId = d.businessId;
  try {
    const r = await requestResearch(businessId);
    if (mine !== drawerSeq || !state.drawer.data) return;
    if (r.searchId === null) {
      // SPEC §19: not the spend owner, so the research waits for PJ's approval; nothing runs (or polls) until then.
      const waiting: DrawerData = { ...state.drawer.data, research: { ...state.drawer.data.research, pendingRequestId: r.approval?.id ?? null } };
      putDrawer(businessId, waiting);
      patchDrawer({ data: waiting });
      toast(r.message ?? 'Sent for approval. Research starts once it is approved.', 'info');
      return;
    }
    const next: DrawerData = { ...state.drawer.data, research: { ...state.drawer.data.research, searchId: r.searchId, status: r.status } };
    putDrawer(businessId, next);
    patchDrawer({ data: next });
    toast(`Research started (search #${r.searchId}). It usually takes a minute or two; the Business panel updates itself.`, 'info');
    scheduleResearchPoll(mine, businessId, 1);
  } catch (e) {
    toast(`Research didn't start: ${errorMessage(e)}`, 'bad');
  }
}

function scheduleResearchPoll(mine: number, businessId: number, n: number): void {
  stopResearchPoll();
  if (n > RESEARCH_POLL_MAX) return;
  researchTimer = setTimeout(() => {
    researchTimer = null;
    if (mine !== drawerSeq) return;
    const membershipId = state.drawer.membershipId;
    prefetchDrawer(businessId, membershipId).then((data) => {
      if (mine !== drawerSeq) return;
      preloadImage(data.website?.screenshotUrl);
      patchDrawer({ data });
      const running = data.research.status !== null && RESEARCH_RUNNING.includes(data.research.status);
      if (!data.researched && running) scheduleResearchPoll(mine, businessId, n + 1);
    }, () => { if (mine === drawerSeq) scheduleResearchPoll(mine, businessId, n + 1); });
  }, RESEARCH_POLL_MS);
}

/** Props for <BusinessDrawer> from the store: `loading` covers the snapshot load and any live check in flight. */
export function drawerProps(s: DialerState): { data: DrawerData | null; live: LiveCheck | null; loading: boolean; error: string | null } {
  const d = s.drawer;
  return { data: d.data, live: d.live, loading: d.loading || d.liveInFlight > 0, error: d.data ? d.liveError : d.error };
}

// ------------------------------------------------------------------ tab close: free the lead for teammates

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    const s = state;
    if (s.item && s.view === 'lead' && canLeaveLead(s.call)) {
      try {
        void fetch('/api/queue/release', {
          method: 'POST', keepalive: true, credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'vd' },
          body: JSON.stringify({ membershipId: s.item.membership.id }),
        });
      } catch { /* the lock expires on its own after 10 minutes */ }
    }
  });
}
